import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner, StatCard } from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";

const CATEGORY_FILTERS = [
  { id: "all", label: "All" },
  { id: "medical", label: "Medical" },
  { id: "surgical", label: "Surgical" },
  { id: "diagnostic", label: "Diagnostic" },
  { id: "allied", label: "Allied Health" },
  { id: "other", label: "Other" },
];

const VIEW_STORAGE_KEY = "library.specialties.view";

export default function LibrarySpecialties() {
  const nav = useNavigate();
  const { isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();

  const [catalog, setCatalog] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_STORAGE_KEY) || "grid";
    } catch {
      return "grid";
    }
  });

  const setViewPersistent = (v) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      // ignore storage errors
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [catRes, statsRes] = await Promise.all([
          api.listSpecialtyCatalog(),
          api.getSpecialtyCatalogStats().catch(() => ({ data: {} })),
        ]);
        setCatalog(catRes.data || []);
        setStats(statsRes.data || {});
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter by category + search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((s) => {
      if (category !== "all" && s.category !== category) return false;
      if (q) {
        const hay = `${s.name} ${s._id} ${s.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [catalog, category, search]);

  // Aggregate totals across filtered set for header stat cards
  const totals = useMemo(() => {
    let templates = 0;
    let protocols = 0;
    let subTenants = 0;
    let withTemplates = 0;
    for (const s of filtered) {
      const st = stats[s._id] || {};
      templates += st.template_count || 0;
      protocols += st.protocol_count || 0;
      subTenants += st.sub_tenant_count || 0;
      if ((st.template_count || 0) > 0) withTemplates++;
    }
    return { templates, protocols, subTenants, withTemplates };
  }, [filtered, stats]);

  // Group by category for grid view
  const grouped = useMemo(() => {
    const g = {};
    for (const s of filtered) {
      const cat = s.category || "other";
      if (!g[cat]) g[cat] = [];
      g[cat].push(s);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => a.name.localeCompare(b.name));
    }
    return g;
  }, [filtered]);

  return (
    <>
      <PageHeader
        title="Agent Groups"
        subtitle={`${catalog.length} agent groups defined. Each group is a named bundle of agents — assign any agent to one or more groups to bundle them for tenants that need that workflow.`}
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Agent Groups" },
        ]}
        actions={
          isLive ? null : (
            <button
              className="btn primary"
              onClick={() => nav("/library/specialties/new")}
            >
              + New group
            </button>
          )
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        {/* Top stat cards */}
        <div className="stat-grid">
          <StatCard
            label="Agent Groups"
            value={filtered.length}
            sub={
              category === "all" && !search
                ? `of ${catalog.length}`
                : `filtered from ${catalog.length}`
            }
          />
          <StatCard
            label="With templates"
            value={totals.withTemplates}
            sub={`${totals.templates} agent templates total`}
          />
          <StatCard
            label="Agent Rules"
            value={totals.protocols}
            sub="across filtered specialties"
          />
          <StatCard
            label="Sub-tenants"
            value={totals.subTenants}
            sub="using filtered specialties"
          />
        </div>

        <div className="filter-row">
          {CATEGORY_FILTERS.map((c) => {
            const count =
              c.id === "all"
                ? catalog.length
                : catalog.filter((s) => s.category === c.id).length;
            return (
              <button
                key={c.id}
                className={`btn sm ${category === c.id ? "primary" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
                <span className="muted" style={{ marginLeft: 4 }}>
                  ({count})
                </span>
              </button>
            );
          })}
          <div className="spacer" />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "6px 12px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              minWidth: 180,
            }}
          />
          <div className="view-toggle">
            <button
              className={view === "grid" ? "active" : ""}
              onClick={() => setViewPersistent("grid")}
              title="Grid view"
            >
              ▦ Grid
            </button>
            <button
              className={view === "list" ? "active" : ""}
              onClick={() => setViewPersistent("list")}
              title="List view"
            >
              ☰ List
            </button>
          </div>
        </div>

        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3>No specialties match</h3>
            <p>Adjust filters or search.</p>
          </div>
        ) : view === "grid" ? (
          <GridView grouped={grouped} stats={stats} prefix={prefix} />
        ) : (
          <ListView filtered={filtered} stats={stats} prefix={prefix} />
        )}
      </div>
    </>
  );
}

// ─── Grid view ───────────────────────────────────────────────────────

function GridView({ grouped, stats, prefix }) {
  return (
    <>
      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} className="mb-24">
          <h3
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--color-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 10,
            }}
          >
            {categoryLabel(cat)}
            <span
              className="muted"
              style={{ marginLeft: 8, fontWeight: 400, textTransform: "none" }}
            >
              ({list.length})
            </span>
          </h3>
          <div className="specialty-grid">
            {list.map((s) => (
              <SpecialtyCard
                key={s._id}
                specialty={s}
                stats={stats[s._id] || {}}
                prefix={prefix}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function SpecialtyCard({ specialty, stats, prefix }) {
  const color = specialty.color || "#666";
  const templates = stats.template_count || 0;
  const protocols = stats.protocol_count || 0;
  const subTenants = stats.sub_tenant_count || 0;

  return (
    <Link
      to={`${prefix}/specialties/${encodeURIComponent(specialty.id || specialty._id)}`}
      className="specialty-card"
      style={{ "--card-accent": color }}
    >
      <span className="cat-pill">{specialty.category}</span>
      <div className="top">
        <div className="tile" style={{ background: color }}>
          {specialty.icon || (specialty.name?.[0] || "?").toUpperCase()}
        </div>
        <div className="title-block">
          <div className="name">{specialty.name}</div>
          <div className="slug">{specialty._id}</div>
        </div>
      </div>
      {specialty.description && (
        <div className="desc">{specialty.description}</div>
      )}
      <div className="stats-row">
        <div className="stat">
          <span className={`num${templates === 0 ? " zero" : ""}`}>{templates}</span>
          <span className="lbl">Templates</span>
        </div>
        <div className="stat">
          <span className={`num${protocols === 0 ? " zero" : ""}`}>{protocols}</span>
          <span className="lbl">Rules</span>
        </div>
        <div className="stat">
          <span className={`num${subTenants === 0 ? " zero" : ""}`}>{subTenants}</span>
          <span className="lbl">Sub-tenants</span>
        </div>
      </div>
    </Link>
  );
}

// ─── List view ───────────────────────────────────────────────────────

function ListView({ filtered, stats, prefix }) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <table className="table" style={{ border: "none" }}>
        <thead>
          <tr>
            <th style={{ width: 52 }}></th>
            <th>Name</th>
            <th>Category</th>
            <th className="right">Templates</th>
            <th className="right">Rules</th>
            <th className="right">Sub-Tenants</th>
            <th>Body Systems</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((s) => {
            const st = stats[s._id] || {};
            return (
              <tr key={s._id}>
                <td>
                  <div
                    className="tile"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: s.color || "#666",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    {s.icon || (s.name?.[0] || "?").toUpperCase()}
                  </div>
                </td>
                <td>
                  <Link to={`${prefix}/specialties/${encodeURIComponent(s.id || s._id)}`}>
                    <strong>{s.name}</strong>
                  </Link>
                  <div className="muted small mono">{s._id}</div>
                </td>
                <td>
                  <span className="badge badge-accent">{s.category}</span>
                </td>
                <td className="right">
                  {st.template_count > 0 ? (
                    st.template_count
                  ) : (
                    <span className="subtle">—</span>
                  )}
                </td>
                <td className="right">
                  {st.protocol_count > 0 ? (
                    st.protocol_count
                  ) : (
                    <span className="subtle">—</span>
                  )}
                </td>
                <td className="right">
                  {st.sub_tenant_count > 0 ? (
                    st.sub_tenant_count
                  ) : (
                    <span className="subtle">—</span>
                  )}
                </td>
                <td>
                  <div className="btn-row" style={{ flexWrap: "wrap" }}>
                    {(s.body_systems || []).length === 0 ? (
                      <span className="subtle">—</span>
                    ) : (
                      s.body_systems.map((b) => (
                        <span key={b} className="badge badge-neutral">
                          {b}
                        </span>
                      ))
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function categoryLabel(cat) {
  const e = CATEGORY_FILTERS.find((c) => c.id === cat);
  return e?.label || cat;
}
